/* CreditNotesCrudCard.tsx
    Une card affichant la gestion des avoirs
    Utilise le composant GenericCrudCard pour lister, ajouter, gérer et supprimer les éléments
    Utilise l'API correspondante pour les opérations de données
*/

import React from 'react';
import { GenericCrudCard } from 'common';
import { CreditNoteData, creditNotesApi } from 'features/creditNotes';
import { faMoneyBillTransfer } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

interface CreditNotesCrudCardProps {
	size?: 'sm' | 'md' | 'lg';
	returnPath?: string;
	returnLabel?: string;
	returnIcon?: IconDefinition;
	returnInvert?: boolean;
	className?: string;
	/** Affiche ou masque l'icône dans l'en-tête de la card. Défaut : true */
	showIcon?: boolean;
	/** Masque le bouton retour. Utile quand la card est intégrée dans une page avec son propre bouton retour. Défaut : false */
	hideReturn?: boolean;
}

// Formatage monétaire EUR
const formatCurrency = (value: number | undefined | null): string => {
	if (value === undefined || value === null) return '—';
	return new Intl.NumberFormat('fr-FR', {
		style: 'currency',
		currency: 'EUR',
	}).format(Number(value));
};

// CreditNotesCrudCard component
export const CreditNotesCrudCard: React.FC<CreditNotesCrudCardProps> = ({
	size = 'sm',
	returnPath = '/app/creditNotes',
	returnLabel,
	returnIcon,
	returnInvert = false,
	className,
	showIcon = true,
	hideReturn = false,
}) => {
	// Détermination si on utilise la version étendue (size !== 'sm')
	const extended = size !== 'sm';

	// Définition des colonnes du tableau
	const columns = [
		// Colonne "ID" uniquement si extended
		...(extended
			? [{ label: 'ID', field: 'id', render: (a: CreditNoteData) => a.id }]
			: []),
		// Colonne "Référence"
		{
			label: 'Référence',
			field: 'referenceNumber',
			render: (a: CreditNoteData) => a.referenceNumber || '',
		},
		// Colonne "Facture client" avec lien vers la fiche facture
		{
			label: 'Facture client',
			field: 'customerInvoice.referenceNumber',
			render: (a: CreditNoteData) => a.customerInvoice?.referenceNumber || '—',
			linkUrl: (a: CreditNoteData): string =>
				a.customerInvoice
					? `/app/customerInvoices/${a.customerInvoice.id}/edit`
					: '',
		},
		// Colonne "Montant HT" calculé (Σ activités + Σ éléments logistiques)
		{
			label: 'Montant HT',
			field: 'none',
			render: (a: CreditNoteData) => formatCurrency(a.totalExclTax),
			exportValue: (a: CreditNoteData): number | string =>
				a.totalExclTax !== undefined ? Number(a.totalExclTax) : '',
		},
		// Colonne "Date de création"
		{
			label: 'Créé le',
			field: 'createdAt',
			filterType: 'date',
			render: (a: CreditNoteData) =>
				a.createdAt ? new Date(a.createdAt).toLocaleDateString('fr-FR') : '—',
			exportValue: (a: CreditNoteData): string =>
				a.createdAt ? new Date(a.createdAt).toLocaleDateString('fr-FR') : '',
		},
	];

	// Colonnes visibles uniquement dans l'export Excel
	const exportOnlyColumns = [
		{
			label: 'Motif',
			exportValue: (a: CreditNoteData): string => a.reason ?? '',
		},
		{
			label: 'Créé par',
			exportValue: (a: CreditNoteData): string => {
				const u = a.createdBy;
				if (!u) return '';
				return [u.firstName, u.name].filter(Boolean).join(' ');
			},
		},
		{
			label: "Nb d'activités",
			exportValue: (a: CreditNoteData): number => a.activities?.length ?? 0,
		},
		{
			label: "Nb d'éléments logistiques",
			exportValue: (a: CreditNoteData): number =>
				a.logisticElements?.length ?? 0,
		},
	];

	// Rendu du composant GenericCrudCard avec les props appropriées
	return (
		<GenericCrudCard<CreditNoteData>
			queryKey="creditNotes"
			title={size === 'sm' ? 'Avoirs' : 'Liste des avoirs'}
			fetchData={(token, options) =>
				creditNotesApi.listPaginated(token, options)
			}
			deleteItem={creditNotesApi.delete}
			tableColumns={columns}
			exportOnlyColumns={exportOnlyColumns}
			addButtonLabel="Ajouter un avoir"
			getAddPath={() => '/app/creditNotes/new'}
			getEditPath={(id) => `/app/creditNotes/${id}/edit`}
			emptyMessage="Aucun avoir trouvé."
			getReturnPath={hideReturn ? undefined : () => returnPath}
			returnLabel={returnLabel}
			returnIcon={returnIcon}
			returnInvert={returnInvert}
			size={size}
			icon={size === 'sm' ? faMoneyBillTransfer : undefined}
			className={className}
			showIcon={showIcon}
		/>
	);
};
export default CreditNotesCrudCard;
